import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../state/AuthContext';
import { getArtistAlbums, getAlbumDetails } from '../api/deezerClient';

const RECENCY_DAYS = 30;
const CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

const EXCLUDED_TYPES = new Set(['compile']);
const REPACKAGE_PATTERN = /\b(best of|greatest hits|anthology|collection|complete works|essential|the very best)\b/i;

const STORAGE_KEY = 'stellar_new_releases_cache';

// Module-level cache to survive navigation; hydrated from localStorage on load
const releasesCache = { key: null, data: null, timestamp: 0 };

try {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    if (parsed && parsed.key && Date.now() - parsed.timestamp < CACHE_TTL_MS) {
      releasesCache.key = parsed.key;
      releasesCache.data = parsed.data;
      releasesCache.timestamp = parsed.timestamp;
    }
  }
} catch {}

function persistCache() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      key: releasesCache.key,
      data: releasesCache.data,
      timestamp: releasesCache.timestamp,
    }));
  } catch {}
}

function isDeezerNumericId(id) {
  return id && /^\d+$/.test(id);
}

/**
 * Parse a "YYYY-MM-DD" date string into a UTC timestamp (ms).
 * Returns NaN for any invalid or out-of-range input.
 * Does NOT rely on new Date(string) which varies across browsers.
 */
function parseReleaseDateUTC(dateStr) {
  if (!dateStr) return NaN;
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return NaN;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return NaN;
  return Date.UTC(year, month - 1, day);
}

function isWithinWindow(dateStr, cutoffMs, nowMs) {
  const ms = parseReleaseDateUTC(dateStr);
  return !isNaN(ms) && ms >= cutoffMs && ms <= nowMs;
}

export function useNewReleases() {
  const { user, favorites, discoveredArtists } = useAuth();
  const [releases, setReleases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef(false);

  // Combine + dedupe artist IDs (only numeric Deezer IDs), keeping name lookup
  const { artistIds, artistNameMap } = useMemo(() => {
    if (!user) return { artistIds: [], artistNameMap: {} };
    const all = [...favorites, ...discoveredArtists];
    const seen = new Set();
    const ids = [];
    const nameMap = {};
    for (const a of all) {
      if (isDeezerNumericId(a.artistId) && !seen.has(a.artistId)) {
        seen.add(a.artistId);
        ids.push(a.artistId);
        nameMap[a.artistId] = a.artistName || '';
      }
    }
    return { artistIds: ids, artistNameMap: nameMap };
  }, [user, favorites, discoveredArtists]);

  const cacheKey = artistIds.join(',');

  useEffect(() => {
    if (!cacheKey) {
      setReleases([]);
      return;
    }

    // Check module cache
    if (releasesCache.key === cacheKey && Date.now() - releasesCache.timestamp < CACHE_TTL_MS) {
      setReleases(releasesCache.data);
      return;
    }

    // Debounce: favorites and discoveredArtists arrive from separate API calls,
    // so cacheKey changes twice in quick succession. Wait for it to stabilize.
    const debounceTimer = setTimeout(() => {
    abortRef.current = false;
    setIsLoading(true);

    (async () => {
      const today = new Date();
      const nowMs = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
      const cutoffMs = nowMs - RECENCY_DAYS * 24 * 60 * 60 * 1000;

      const results = await Promise.allSettled(
        artistIds.map(id => getArtistAlbums(id, 50))
      );

      if (abortRef.current) return;

      const allAlbums = [];
      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        if (r.status !== 'fulfilled') continue;
        for (const album of r.value) {
          if (EXCLUDED_TYPES.has(album.type)) continue;
          if (REPACKAGE_PATTERN.test(album.title)) continue;
          if (!isWithinWindow(album.releaseDate, cutoffMs, nowMs)) continue;
          allAlbums.push({
            ...album,
            artistName: album.artistName || artistNameMap[artistIds[i]] || '',
          });
        }
      }

      // Dedupe by album ID
      const seen = new Set();
      const candidates = [];
      for (const album of allAlbums) {
        if (!seen.has(album.id)) {
          seen.add(album.id);
          candidates.push(album);
        }
      }

      // Cross-validate release dates via individual album details
      const detailResults = await Promise.allSettled(
        candidates.map(a => getAlbumDetails(a.id))
      );

      if (abortRef.current) return;

      const unique = [];
      for (let i = 0; i < candidates.length; i++) {
        const detail = detailResults[i];
        if (detail.status !== 'fulfilled' || !detail.value) {
          unique.push(candidates[i]);
          continue;
        }
        const verifiedDate = detail.value.releaseDate;
        if (!isWithinWindow(verifiedDate, cutoffMs, nowMs)) continue;
        unique.push({ ...candidates[i], releaseDate: verifiedDate });
      }

      unique.sort((a, b) => parseReleaseDateUTC(b.releaseDate) - parseReleaseDateUTC(a.releaseDate));

      releasesCache.key = cacheKey;
      releasesCache.data = unique;
      releasesCache.timestamp = Date.now();
      persistCache();

      setReleases(unique);
      setIsLoading(false);
    })();
    }, 300);

    return () => { clearTimeout(debounceTimer); abortRef.current = true; };
  }, [cacheKey, artistIds, artistNameMap]);

  return { releases, isLoading };
}
